import { appendAuthHeader, withReplacedAuthHeader } from '@execonline-inc/unified-auth.private';
import { assertNever } from '@kofno/piper';
import { Header, HttpError, RequestBuilder, del, header, toHttpTask } from 'ajaxian';
import { Decoder } from 'jsonous';
import { ok } from 'resulty';
import { Task } from 'taskarian';
import { readVarT } from '../Assertions/environments';
import { findLinkT } from '../LinkyLinky';
import { Link, Rel } from '../Resource/Types';
import { handleRecovery } from '../Session';
import { requestSessionData } from '../Session/Reactions';
import { sessionStore } from '../Session/Store';

export interface MissingApplicationId {
  kind: 'missing-application-id';
}

export interface MissingApiCompatibility {
  kind: 'missing-api-compatibility';
}

export type AppyError = HttpError | MissingApplicationId | MissingApiCompatibility;

const missingApplicationId = (): MissingApplicationId => ({ kind: 'missing-application-id' });

const missingApiCompatibility = (): MissingApiCompatibility => ({
  kind: 'missing-api-compatibility',
});

const appId = readVarT('VITE_APPLICATION_ID').mapError<AppyError>(missingApplicationId);

const apiCompatibility =
  readVarT('VITE_API_COMPATIBILITY').mapError<AppyError>(missingApiCompatibility);

const applicationIdHeader = appId.map((id) => header('APPLICATION-ID', id));

const apiCompatibilityHeader = apiCompatibility.map((compatibility) =>
  header('api-compatibility', compatibility),
);

const requestBuilderData = (link: Link, payload: {}): {} | undefined => {
  switch (link.method) {
    case 'get':
    case 'head':
    case 'options':
      return undefined;
    case 'delete':
    case 'patch':
    case 'post':
    case 'put':
      return payload;
  }
};

export const clientHeadersT = (): Task<AppyError, Header[]> =>
  Task.succeed<AppyError, Header[]>([])
    .assign('appIdHeader', applicationIdHeader)
    .assign('apiCompHeader', apiCompatibilityHeader)
    .map(({ appIdHeader, apiCompHeader }) => [appIdHeader, apiCompHeader])
    .map(appendAuthHeader(sessionStore.sessionToken));

const toRequest = (link: Link, payload: {}, headers: Array<Header>): RequestBuilder<string> =>
  new RequestBuilder({
    url: link.href,
    decoder: ok,
    data: requestBuilderData(link, payload),
    method: link.method,
    timeout: 0,
    headers,
    withCredentials: true,
  });

export const request = (link: Link, payload: {}): Task<AppyError, RequestBuilder<string>> =>
  clientHeadersT().map((headers) => toRequest(link, payload, headers));

export const retryOnSessionExpiration = <T>(
  originalRequest: Task<AppyError, RequestBuilder<T>>,
): Task<AppyError, T> =>
  new Task((reject, resolve) => {
    return originalRequest.andThen(toHttpTask).fork((err) => {
      switch (err.kind) {
        case 'bad-status':
          if (err.response.status === 401) {
            requestSessionData().fork(
              () => reject(err),
              handleRecovery((sessionData): void => {
                sessionStore.present(sessionData);
                originalRequest
                  .map(withReplacedAuthHeader(sessionData))
                  .andThen(toHttpTask)
                  .fork(reject, resolve);
              }),
            );
          } else {
            reject(err);
          }
          break;
        case 'bad-payload':
        case 'bad-url':
        case 'missing-application-id':
        case 'missing-api-compatibility':
        case 'network-error':
        case 'timeout':
          reject(err);
          break;
        default:
          assertNever(err);
      }
    }, resolve);
  });

export function fetchJson(link: Link): Task<AppyError, string> {
  return retryOnSessionExpiration(request(link, {}));
}

export function decodeOnSelect<T>(decoder: Decoder<T>): (jsonData: string) => T | undefined {
  return (jsonData: string) =>
    decoder.decodeJson(jsonData).cata({
      Ok: (value) => value,
      Err: (msg) => {
        console.error('Failed to decode JSON:', msg);
        return undefined;
      },
    });
}

export function queryTaskAsPromise(rel: Rel, links: ReadonlyArray<Link>): Promise<string> {
  return Task.succeed(links).andThen(findLinkT(rel)).andThen(fetchJson).resolve();
}

export const callApi =
  <T>(decoder: Decoder<T>, payload: {}) =>
  (link: Link): Task<AppyError, T> =>
    retryOnSessionExpiration(request(link, payload).map((r) => r.withDecoder(decoder.toJsonFn())));

/**
 * @deprecated Use the `useFetch` hook.
 */
export const deleteToApi = (link: Link): Task<AppyError, string> =>
  retryOnSessionExpiration(
    clientHeadersT().map((headers) => {
      const request = del(link.href).setWithCredentials(true);
      return headers.reduce((req, header) => req.withHeader(header), request);
    }),
  );
